import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ClaimStatus, EhrClaim, ProviderWithAperoId } from '../../interfaces/billing';
import { SelectOption } from '../../interfaces/common';
import { AuthService, BillingService } from '../../services';
import { StyledTheme } from '../../styles/styleTheme';
import { Roles } from '../../util/constants';
import { formatDateToDayAndDate } from '../../util/dateFormat';
import {
  getAperoUrl,
  getDrChronoAppointmentUrl,
  getDrChronoPatientUrl,
  getEnaraUrl,
} from '../../util/url';
import { CustomModal } from '../common/CustomModal/CustomModal';
import { ClaimLogs } from './ClaimLogs';

interface ClaimDetailsProps {
  clinics: SelectOption[];
  claim: EhrClaim;
  forceEhrFetch: boolean;
  onForceEhrFetch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  onResync: () => Promise<void>;
  onBack?: () => void;
  onClaimUpdate?: (claim: EhrClaim) => void;
  onClaimDeleted?: (claimId: number) => void;
}

export const ClaimDetails: React.FC<ClaimDetailsProps> = ({
  clinics,
  claim,
  forceEhrFetch,
  onForceEhrFetch,
  onClose,
  onResync,
  onBack,
  onClaimUpdate,
  onClaimDeleted,
}) => {
  const [onResyncDisabled, setOnResyncDisabled] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const canDeleteClaim = AuthService.checkRoleAccess(Roles.Operations);

  // Provider override state
  const [isEditingProvider, setIsEditingProvider] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderWithAperoId | null>(null);
  const [providers, setProviders] = useState<ProviderWithAperoId[]>([]);
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [message, setMessage] = useState('');

  const clinic = clinics.find((c) => c.value === `${claim.clinicId}`);

  // Fetch providers when editing starts
  useEffect(() => {
    const fetchProviders = async () => {
      if (!isEditingProvider || !claim?.clinicId) return;

      setIsLoadingProviders(true);
      try {
        const response = await new BillingService().getProvidersByClinic(claim.clinicId);
        if (response?.providers) {
          setProviders(response.providers);
          // Find and select the current override provider if exists
          if (claim.overrides?.aperoProviderId) {
            const currentProvider = response.providers.find(
              (p) => p.apero_provider_id === claim.overrides?.aperoProviderId,
            );
            setSelectedProvider(currentProvider || null);
          }
        }
      } catch (err) {
        console.error(err);
        setMessage('Error loading providers');
      } finally {
        setIsLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [isEditingProvider, claim?.clinicId, claim?.overrides?.aperoProviderId]);

  const handleToggleLogs = () => {
    setShowLogsDialog(!showLogsDialog);
  };

  const handleOnResync = async () => {
    setOnResyncDisabled(true);
    await onResync();
    setOnResyncDisabled(false);
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) {
      return;
    }
    setIsDeleting(true);
    try {
      const response = await new BillingService().deleteEhrClaim(claim.id);
      if (response?.success) {
        setMessage('Claim deleted');
        onClaimDeleted?.(claim.id);
        onClose();
      } else {
        setMessage(response?.message || 'Failed to delete claim');
      }
    } catch (err) {
      console.error(err);
      setMessage('Failed to delete claim');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveProviderOverride = async () => {
    if (!claim) return;

    setIsSavingOverride(true);
    try {
      const overrides = selectedProvider?.apero_provider_id
        ? { aperoProviderId: selectedProvider.apero_provider_id }
        : { aperoProviderId: null as unknown as string };

      const response = await new BillingService().updateClaimOverrides(claim.id, overrides);

      if (response?.success) {
        setMessage('Provider override saved');
        setIsEditingProvider(false);
        if (onClaimUpdate && response.claim) {
          onClaimUpdate(response.claim);
        }
      } else {
        setMessage(response?.message || 'Error saving provider override');
      }
    } catch (err) {
      setMessage('Error saving provider override');
    } finally {
      setIsSavingOverride(false);
    }
  };

  const handleCancelEditProvider = () => {
    setSelectedProvider(null);
    setIsEditingProvider(false);
  };

  return (
    <Box className='event-details' display='flex' flexDirection='column' height='100%'>
      {onBack && (
        <Box display='flex' justifyContent='space-between'>
          <Button onClick={onBack} style={{ color: StyledTheme.tealDark }}>
            <ArrowBackIcon /> Back
          </Button>
          <Button onClick={onClose} style={{ color: StyledTheme.tealDark }}>
            <CloseIcon /> Close
          </Button>
        </Box>
      )}
      <Box p={2} flexGrow={1}>
        <Box style={{ textAlign: 'center' }}>
          <Typography variant='h6'>{formatDateToDayAndDate(claim.serviceDate)}</Typography>
        </Box>
        {clinic && (
          <Box mb={2}>
            <Typography sx={{ fontWeight: 'bolder' }}>Clinic:</Typography>
            <Typography>
              <Link href={getEnaraUrl(`/activeadmin/clinics/${claim?.clinicId}`)} target='_blank'>
                {clinic.label}
              </Link>
            </Typography>
          </Box>
        )}
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Claim ID:</Typography>
          <Typography>
            <Link href={`/#/claims/${claim.id}/show`} target='_blank'>
              {claim.id}
            </Link>
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>DrChrono Patient:</Typography>
          <Typography>
            {claim?.ehrAppointmentData?.appointment?.patient ? (
              <Link
                href={getDrChronoPatientUrl(claim?.ehrAppointmentData?.appointment.patient)}
                target='_blank'>
                {claim?.corePatient?.firstName
                  ? `${claim?.corePatient?.firstName} ${claim?.corePatient?.lastName}`
                  : 'Name not available'}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>DrChrono Appointment:</Typography>
          <Typography>
            {claim?.ehrAppointmentData?.appointment?.id ? (
              <Link
                href={getDrChronoAppointmentUrl(claim?.ehrAppointmentData?.appointment?.id)}
                target='_blank'>
                {claim?.ehrAppointmentData?.appointment?.id || 'N/A'}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Core appointment:</Typography>
          <Typography>
            {claim?.coreAppointmentData?.id ? (
              <Link
                href={getEnaraUrl(`/activeadmin/schedulings/${claim?.coreAppointmentData?.id}`)}
                target='_blank'>
                {claim?.coreAppointmentData?.id || 'N/A'}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Core Patient:</Typography>
          <Typography>
            {claim?.corePatient ? (
              <Link
                href={getEnaraUrl(`/activeadmin/users/${claim.corePatient.id}`)}
                target='_blank'>
                {`${claim.corePatient.firstName} ${claim.corePatient.lastName}`.trim()}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Core Provider:</Typography>
          <Typography>
            {claim?.coreProvider?.firstName ? (
              <Link
                href={getEnaraUrl(`/activeadmin/users/${claim?.coreProvider?.id}`)}
                target='_blank'>
                {`${claim?.coreProvider?.firstName} ${claim?.coreProvider?.lastName}`.trim()}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography sx={{ fontWeight: 'bolder' }}>Apero invoice:</Typography>
          <Typography>
            {claim?.aperoInvoiceData?.invoice?.id ? (
              <Link
                href={getAperoUrl(`/invoices/${claim?.aperoInvoiceData?.invoice?.id}`)}
                target='_blank'>
                {claim?.aperoInvoiceData?.invoice?.id}
              </Link>
            ) : (
              'N/A'
            )}
          </Typography>
        </Box>

        {/* Provider Override Section */}
        <Box
          mb={2}
          p={1.5}
          sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
            <Typography sx={{ fontWeight: 'bolder', fontSize: '0.875rem' }}>
              Apero Provider Override
            </Typography>
            <Tooltip title='Override the Apero provider used when syncing this claim'>
              <span>
                {!isEditingProvider && (
                  <IconButton
                    size='small'
                    onClick={() => setIsEditingProvider(true)}
                    sx={{ color: StyledTheme.tealDark, padding: '2px' }}>
                    <EditIcon fontSize='small' />
                  </IconButton>
                )}
              </span>
            </Tooltip>
          </Box>

          {isEditingProvider ? (
            <Box display='flex' flexDirection='column' gap={1}>
              <Autocomplete
                size='small'
                fullWidth
                loading={isLoadingProviders}
                options={providers.filter((p) => p.apero_provider_id)}
                value={selectedProvider}
                onChange={(_, newValue) => setSelectedProvider(newValue)}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select provider...'
                    size='small'
                    disabled={isSavingOverride}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingProviders ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={isSavingOverride}
              />
              <Box display='flex' gap={1}>
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  fullWidth
                  onClick={handleSaveProviderOverride}
                  disabled={isSavingOverride || isLoadingProviders}
                  startIcon={isSavingOverride ? <CircularProgress size={14} /> : <SaveIcon />}
                  sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                  Save
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  fullWidth
                  onClick={handleCancelEditProvider}
                  disabled={isSavingOverride}
                  sx={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.875rem' }}>
              {claim?.overrides?.aperoProviderId ? (
                <Link
                  href={getAperoUrl(`/users/${claim.overrides.aperoProviderId}`)}
                  target='_blank'>
                  {claim.overrides.aperoProviderId}
                </Link>
              ) : (
                <span style={{ color: '#999' }}>Not set</span>
              )}
            </Typography>
          )}
        </Box>

        <Box mb={2}>
          <Button
            size='large'
            fullWidth
            onClick={handleToggleLogs}
            style={{
              background: StyledTheme.grayDark,
              color: StyledTheme.white,
              textTransform: 'capitalize',
            }}>
            <FormatAlignJustifyIcon /> View logs
          </Button>
        </Box>
        {claim.status !== ClaimStatus.Processed && (
          <Box mb={2}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    style={{ color: StyledTheme.tealDark }}
                    value={forceEhrFetch}
                    onChange={onForceEhrFetch}
                    checked={forceEhrFetch}
                  />
                }
                label='Force EHR appointment download?'
              />
            </FormGroup>

            <Button
              size='large'
              fullWidth
              color='primary'
              variant='contained'
              disabled={onResyncDisabled}
              onClick={handleOnResync}>
              Attempt resync
            </Button>
          </Box>
        )}

        {canDeleteClaim && (
          <Box mb={2}>
            <CustomModal
              showChildrenDefault={false}
              modalProps={{
                style: { width: '480px', maxWidth: '90vw', height: 'auto', padding: '24px' },
              }}
              buttonProps={{
                buttonText: 'Delete claim',
                disabled: isDeleting,
                fullWidth: true,
                size: 'large',
                sx: {
                  '&.custom-modal-button': {
                    backgroundColor: '#d32f2f',
                    color: StyledTheme.white,
                    textTransform: 'capitalize',
                    '&:hover': { backgroundColor: '#b71c1c' },
                  },
                },
              }}
              actionButtons={{
                cancelButtonProps: { buttonText: 'Cancel' },
                continueButtonProps: {
                  buttonText: 'Confirm delete',
                  sx: { backgroundColor: '#d32f2f', color: StyledTheme.white },
                },
              }}
              onContinue={handleConfirmDelete}>
              <Box p={2}>
                <Typography variant='h6' gutterBottom>
                  Delete claim {claim.id}?
                </Typography>
                <Typography variant='body2'>
                  It will be hidden from the calendar and skipped on future syncs. This action
                  cannot be undone from the dashboard.
                </Typography>
              </Box>
            </CustomModal>
          </Box>
        )}
      </Box>

      <Dialog open={showLogsDialog} onClose={handleToggleLogs} maxWidth='md' fullWidth>
        <DialogTitle>
          Claim logs
          <IconButton
            aria-label='close'
            onClick={handleToggleLogs}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ClaimLogs logs={claim.ehrClaimLogs} />
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!message}
        autoHideDuration={5000}
        onClose={() => setMessage('')}
        message={message}
      />
    </Box>
  );
};
